import type { HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { redirect } from '@remix-run/node';
import { useLoaderData, useRouteLoaderData } from '@remix-run/react';

import StaticPage from '~/components/StaticPage';
import { staticPageClientLoader } from '~/components/StaticPage/staticPageClientLoader';
import type { RootLoaderData } from '~/root';
import formatTagDisplayName from '~/utils/formatTagDisplayName';
import getStaticPageMetaData from '~/utils/getStaticPageMetaData';
import getUnnormalizedCategory from '~/utils/getUnnormalizedCategory';
import { BASE_CACHE_TAG } from '~/utils/setCacheHeaders/index.server';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

import type { StaticPageLoaderData } from '../../services/static_page';
import { staticPageLoader } from '../../services/static_page';

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': loaderHeaders.get('Cache-Tag') || BASE_CACHE_TAG,
  };
};

export const meta: MetaFunction<typeof loader> = (args) => {
  const { params, data } = args;
  const tag = params.tag || '';
  const tagDisplayName = data?.page?.content_tag?.display;
  const tagName = tagDisplayName || formatTagDisplayName(tag);

  const overrideData = {
    title: tagName,
    description: data?.page?.content_tag?.description,
    image: data?.page?.content_tag?.image_url,
  };

  return getStaticPageMetaData(args, overrideData);
};
export const loader: LoaderFunction = async ({ request, params, context }) => {
  const tag = params?.tag || '';
  // Remix decodes %XX sequences but not +. In URL path segments, + is a literal character,
  // but users often type it expecting a space. Decode it so /t/AI+101 matches tag "AI 101".
  // getUnnormalizedCategory is a no-op on server (returns input unchanged), kept for parity
  // with formatTagDisplayName and in case future client-side usage needs it.
  const tagText = getUnnormalizedCategory(tag).replace(/\+/g, ' ');

  const response = await staticPageLoader({
    request,
    context,
    pageRequestParams: {
      pathname: 't/$tag',
      tagPageHeaderDisplay: tagText,
    },
  });

  // Tags can also resolve by display name (e.g. encoded/mixed-case URLs); canonicalize to the slug.
  const data = (await response.clone().json()) as StaticPageLoaderData;
  const canonicalSlug = data.page?.content_tag?.slug;
  if (canonicalSlug && canonicalSlug !== tag) {
    const url = new URL(request.url);
    url.pathname = `/t/${canonicalSlug}`;
    return redirect(`${url.pathname}${url.search}`, 301);
  }

  return response;
};

export const clientLoader = staticPageClientLoader;

const Tag = () => {
  const { page } = useLoaderData<typeof loader>();
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  return <StaticPage page={page} site={site} />;
};

export const shouldRevalidate = shouldRevalidateLoaderData;

export default Tag;
